import type { Mapping, Module } from "@evidenceb/gameplay-interfaces";
import type { Filter } from "../../design-system-components/ResourceFilter/Components/filterTypes";
import { isParentOfCheckedChild } from "../../design-system-components/ResourceFilter/Components/utils";
import type { Data, ModuleGroup } from "../../interfaces/Data";
import type { MappingNode } from "../../interfaces/MappingNode";
import { getObjectiveById } from "../../utils/dataRetrieval";
import { getAllBranchTags, getMappingNode } from "../../utils/mapping";
import { isDisplayed } from "../../utils/visibility";
export enum ResourceState {
  TO_DO = "TO_DO",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
}

export type PerState<T> = {
  [state in ResourceState]: T;
};

export const getModuleState = (
  isModuleFinished: boolean,
  hasStatements: boolean,
): ResourceState => {
  if (isModuleFinished) return ResourceState.DONE;
  if (!hasStatements) return ResourceState.TO_DO;
  return ResourceState.IN_PROGRESS;
};

export const filterModules = (
  data: Pick<Data, "modules" | "objectives">,
  filters: Filter[],
  mappingTree: MappingNode,
  emptyIfNoFilter = false,
): Module[] => {
  if (filters.length === 0) return emptyIfNoFilter ? [] : data.modules;
  const mappingBranchesLeaves = getMappingLeaves(filters, mappingTree);
  const descendants = mappingBranchesLeaves.flatMap((leaf) =>
    getDescendantNodeIds(leaf, mappingTree),
  );
  return data.modules.filter((mod) => {
    const objectives = mod.objectiveIds
      .map((objId) => getObjectiveById(objId, data))
      .filter(isDisplayed);
    return (
      objectives.some((obj) => learningSetMatchesFilters(obj, descendants)) ||
      learningSetMatchesFilters(mod, descendants)
    );
  });
};

export const generateModuleGroups = (
  modulesFromData?: Module[],
  buildingBlocks?: { moduleIds: string[]; data: Data },
): ModuleGroup[] => {
  const modules =
    modulesFromData ??
    buildingBlocks?.data.modules.filter((mod) =>
      buildingBlocks.moduleIds.includes(mod.id),
    );

  if (!modules) return [];

  const groupedModules: Record<string, Module[]> = modules.reduce(
    (acc: Record<string, Module[]>, module: Module) => {
      const group = module.title.group_name || "ungrouped";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(module);
      return acc;
    },
    {},
  );
  return Object.entries(groupedModules).map(([groupName, modules]) => ({
    groupName: groupName,
    modules: modules,
  }));
};

export const filterLearningSets = <T extends { mapping_nodes?: Mapping }>(
  learningSets: T[],
  filters: Pick<Filter, "id">[],
  mappingTree: MappingNode,
  emptyIfNoFilter: boolean = false,
): T[] => {
  if (filters.length === 0) return emptyIfNoFilter ? [] : learningSets;
  const mappingLeaves = getMappingLeaves(filters, mappingTree);
  const descendants = mappingLeaves.flatMap((leaf) =>
    getDescendantNodeIds(leaf, mappingTree),
  );
  return learningSets.filter((set) =>
    learningSetMatchesFilters(set, descendants),
  );
};

export const learningSetMatchesFilters = (
  resource: { mapping_nodes?: Mapping },
  filters: string[],
): boolean => {
  return (
    resource.mapping_nodes?.mapping?.some((nodeId) =>
      filters.includes(nodeId),
    ) ?? false
  );
};

export const getDescendantNodeIds = (
  nodeId: string,
  mappingTree: MappingNode,
): string[] => {
  const filterNode = getMappingNode(nodeId, mappingTree);
  if (!filterNode) throw new Error(`Cannot find node with id ${nodeId}`);
  return getAllBranchTags(filterNode);
};

export const getMappingLeaves = (
  filters: Pick<Filter, "id">[],
  mappingTree: MappingNode,
): string[] => {
  return filters
    .filter((filter) => !isParentOfCheckedChild(filter, filters, mappingTree))
    .map((filter) => filter.id);
};
